import type { PurchaseOrderItem, PoGstMode } from '../types';
import { computePoTotals } from './nzGst';

export function newPoLine(overrides?: Partial<PurchaseOrderItem>): PurchaseOrderItem {
  return {
    lineId: `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    productName: '',
    quantity: 1,
    costPerUnit: 0,
    unit: 'units',
    ...overrides,
  };
}

/** Keep 0.5 kg as 0.5, not a long float. */
export function roundQty(quantity: number): number {
  if (!Number.isFinite(quantity)) return 0;
  return Math.round((quantity + Number.EPSILON) * 1000) / 1000;
}

const MEASURE_BASE: Record<string, { group: 'weight' | 'volume'; perBase: number }> = {
  g: { group: 'weight', perBase: 1 },
  kg: { group: 'weight', perBase: 1000 },
  ml: { group: 'volume', perBase: 1 },
  L: { group: 'volume', perBase: 1000 },
};

/**
 * Switch kg ↔ g or L ↔ ml without changing the line amount.
 * 0.5 kg at $10/kg becomes 500 g at $0.01/g. Staying on kg leaves 0.5 as 0.5.
 */
export function convertLineMeasure(
  quantity: number,
  unitPrice: number,
  fromUnit: string | undefined,
  toUnit: string,
): { quantity: number; unitPrice: number } | null {
  if (!fromUnit || fromUnit === toUnit) return null;
  const from = MEASURE_BASE[fromUnit];
  const to = MEASURE_BASE[toUnit];
  if (!from || !to || from.group !== to.group) return null;
  const factor = from.perBase / to.perBase;
  if (!(factor > 0)) return null;
  return {
    quantity: roundQty(quantity * factor),
    unitPrice: unitPrice > 0 ? Math.round((unitPrice / factor + Number.EPSILON) * 1e6) / 1e6 : 0,
  };
}

/** Weight is priced per kg; volume per litre. Packs stay on their own unit. */
export function defaultPriceUnit(quantityUnit: string | undefined): string {
  if (quantityUnit === 'g' || quantityUnit === 'kg') return 'kg';
  if (quantityUnit === 'ml' || quantityUnit === 'L') return 'L';
  return quantityUnit || 'units';
}

export function priceUnitOptions(quantityUnit: string | undefined): string[] {
  if (quantityUnit === 'g' || quantityUnit === 'kg') return ['kg', 'g'];
  if (quantityUnit === 'ml' || quantityUnit === 'L') return ['L', 'ml'];
  return [quantityUnit || 'units'];
}

/** 500 g quoted per kg is 0.5. Same unit, or an unrelated unit, leaves the quantity as entered. */
export function quantityInPriceUnit(
  quantity: number,
  quantityUnit: string | undefined,
  priceUnit: string | undefined,
): number {
  const qty = Number(quantity) || 0;
  const from = quantityUnit || 'units';
  const to = priceUnit || from;
  if (from === to) return qty;
  const converted = convertLineMeasure(qty, 0, from, to);
  return converted ? converted.quantity : qty;
}

/** Line amount when the price unit can differ from the quantity unit. */
export function lineAmount(
  quantity: number,
  quantityUnit: string | undefined,
  unitPrice: number,
  priceUnit?: string,
): number {
  return quantityInPriceUnit(quantity, quantityUnit, priceUnit) * (Number(unitPrice) || 0);
}

/** $12.50 per kg written onto a gram line is $0.0125 per g. */
export function priceInQuantityUnit(
  unitPrice: number,
  priceUnit: string | undefined,
  quantityUnit: string | undefined,
): number {
  const price = Number(unitPrice) || 0;
  if (!(price > 0)) return 0;
  const to = quantityUnit || 'units';
  const from = priceUnit || to;
  if (from === to) return price;
  const converted = convertLineMeasure(1, price, from, to);
  return converted ? converted.unitPrice : price;
}

/** Nearest cent. 1.518 and 1.5180000000000002 both become 1.52. */
export function roundMoney(amount: number): number {
  if (!Number.isFinite(amount)) return 0;
  return Math.round((amount + Number.EPSILON) * 100) / 100;
}

/** Known invoice/PO price only. Zero means the price is not known yet. */
export function knownUnitCost(costPerUnit: number | undefined): number | undefined {
  if (costPerUnit == null || !(costPerUnit > 0)) return undefined;
  const rounded = roundMoney(costPerUnit);
  return rounded > 0 ? rounded : undefined;
}

export function poLineTotal(line: PurchaseOrderItem): number {
  return (line.quantity || 0) * (line.costPerUnit || 0);
}

/** Sum of line totals; when gstMode is set returns total inc-GST. */
export function poOrderTotal(items: PurchaseOrderItem[], gstMode?: PoGstMode): number {
  if (gstMode) {
    return computePoTotals(items, gstMode).totalIncGst;
  }
  return items.reduce((sum, line) => sum + poLineTotal(line), 0);
}
